import * as msal from '@azure/msal-browser';
import { EventEmitter } from 'events';
import { AxiosError } from 'axios';

const forgotPasswordErrorCode = 'AADB2C90118';
const loginEvent = 'loginEvent';
const TOKEN_COOKIE_NAME = 'auth_token';

export class AuthService extends EventEmitter {
  public isAuthenticated = false;
  private msalInstance: msal.PublicClientApplication;
  private scopes: string[] = ['openid', 'profile'];
  private token: msal.AuthenticationResult | null = null;

  constructor() {
    super();
    const msalConfig: msal.Configuration = {
      auth: {
        authority: `https://${process.env.VUE_APP_AZUREADB2C_INSTANCE}/${process.env.VUE_APP_AZUREADB2C_DOMAIN}/${process.env.VUE_APP_AZUREADB2C_SIGNUPSIGNIN_POLICY_ID}`,
        clientId: process.env.VUE_APP_CLIENT_ID ?? '',
        knownAuthorities: [`${process.env.VUE_APP_AZUREADB2C_INSTANCE}`],
        postLogoutRedirectUri: `${window.location.origin}/login`,
        redirectUri: `${window.location.origin}/callback`,
      },
      system: {
        loggerOptions: {
          loggerCallback: (level: msal.LogLevel, message: string, containsPii: boolean): void => {
            if (containsPii) return;
            switch (level) {
              case msal.LogLevel.Error:
                console.error(message);
                break;
              case msal.LogLevel.Info:
                console.info(message);
                break;
              case msal.LogLevel.Verbose:
                console.debug(message);
                break;
              case msal.LogLevel.Warning:
                console.warn(message);
                break;
            }
          },
          piiLoggingEnabled: false,
          logLevel: msal.LogLevel.Verbose,
        },
      },
    };
    this.msalInstance = new msal.PublicClientApplication(msalConfig);
    const accounts: msal.AccountInfo[] = this.msalInstance.getAllAccounts();
    this.isAuthenticated = accounts && accounts.length > 0;

    // Try to restore token from cookie first
    const savedToken = this.getTokenFromCookie();
    if (savedToken) {
      this.token = savedToken;
      this.isAuthenticated = true;
      this.emit(loginEvent);
    } else {
      // If no cookie, handle redirect promise
      this.msalInstance
        .handleRedirectPromise()
        .then((token: msal.AuthenticationResult | null) => {
          this.token = token;
          if (token) {
            this.saveTokenToCookie(token);
          }
          this.emit(loginEvent);
        })
        .catch((err: any) => {
          if (err.errorMessage && err.errorMessage.indexOf(forgotPasswordErrorCode) > -1) {
            this.resetPassword();
          } else {
            console.error(err);
            this.handleAuthError(err);
          }
        });
    }
  }

  private saveTokenToCookie(token: msal.AuthenticationResult): void {
    const tokenData = JSON.stringify(token);
    const date = new Date();
    // Set cookie expiration to 1 hour before token expiration
    if (token.expiresOn) {
      date.setTime(date.getTime() + (token.expiresOn.getTime() - date.getTime() - 3600000));
    } else {
      // If no expiration, set cookie to expire in 1 hour
      date.setTime(date.getTime() + 3600000);
    }
    document.cookie = `${TOKEN_COOKIE_NAME}=${tokenData};expires=${date.toUTCString()};path=/;SameSite=Strict`;
  }

  private getTokenFromCookie(): msal.AuthenticationResult | null {
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find(cookie => cookie.trim().startsWith(`${TOKEN_COOKIE_NAME}=`));
    
    if (!tokenCookie) return null;

    try {
      const tokenData = tokenCookie.split('=')[1];
      const token = JSON.parse(tokenData);
      
      // Check if token is expired
      if (token.expiresOn && new Date(token.expiresOn) <= new Date()) {
        this.removeTokenCookie();
        return null;
      }

      return token;
    } catch (error) {
      console.error('Error parsing token from cookie:', error);
      this.removeTokenCookie();
      return null;
    }
  }

  private removeTokenCookie(): void {
    document.cookie = `${TOKEN_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }

  public checkSignedIn(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const token = this.getTokenFromCookie();
        if (token) {
          this.token = token;
          resolve();
        } else {
          // Try to get a new token silently
          await this.refreshTokenAsync();
          if (this.token) {
            resolve();
          } else {
            reject();
          }
        }
      } catch (error) {
        console.error('Error checking sign in status:', error);
        reject(error);
      }
    });
  }

  public getToken() {
    return this.token;
  }

  public async refreshTokenAsync(): Promise<void> {
    const request = {
      account: this.msalInstance.getAllAccounts()[0],
      scopes: this.scopes,
    };
    try {
      this.token = await this.msalInstance.acquireTokenSilent(request);
      if (this.token) {
        this.saveTokenToCookie(this.token);
      }
    } catch (error) {
      if (error instanceof msal.InteractionRequiredAuthError) {
        this.msalInstance.acquireTokenRedirect(request).catch((error) => {
          console.error('Error acquiring token redirect:', error);
          this.handleAuthError(error);
        });
      } else {
        console.error('Error refreshing token:', error);
        this.handleAuthError(error);
      }
    }
  }

  private handleAuthError(error: any): void {
    if (error && typeof error === 'object' && 'response' in error) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        this.removeTokenCookie();
        this.token = null;
        this.isAuthenticated = false;
        this.emit('loginEvent');
      } else if (error.response?.status === 403) {
        // Forbidden - user doesn't have permission
        console.error('Access forbidden:', error);
      } else if (!error.response) {
        // Network error
        console.error('Network error:', error);
      }
    } else {
      console.error('Authentication error:', error);
    }
  }

  private resetPassword() {
    try {
      this.msalInstance.loginRedirect({
        authority: `https://${process.env.VUE_APP_AZUREADB2C_INSTANCE}/${process.env.VUE_APP_AZUREADB2C_DOMAIN}/${process.env.VUE_APP_AZUREADB2C_RESETPASSWORD_POLICY_ID}`,
        scopes: this.scopes
      });
    } catch (passwordResetError) {
      console.error('Error resetting password:', passwordResetError);
      this.handleAuthError(passwordResetError);
    }
  }

  public signIn() {
    try {
      const loginRequest: msal.RedirectRequest = { scopes: this.scopes };
      this.msalInstance.loginRedirect(loginRequest);
    } catch (err: any) {
      if (err.errorMessage && err.errorMessage.indexOf(forgotPasswordErrorCode) > -1) {
        this.resetPassword();
      } else {
        this.isAuthenticated = false;
        this.handleAuthError(err);
      }
    }
  }

  public async signOut() {
    this.removeTokenCookie();
    await this.msalInstance.logoutRedirect();
  }
}

const service = new AuthService();
service.setMaxListeners(5);

export default service;
